import { illustrationFor } from "@shared/brand.types";
import type { ReactNode } from "react";
import { FieldLabel } from "./ds/EditableField";
import { Eyebrow } from "./ds/Eyebrow";
import { SplitLayout } from "./SplitLayout";

/**
 * Le cadre des écrans qui demandent un code.
 *
 * Ils n'ont pas de marque d'école à porter : au moment où ils s'affichent, on ne
 * sait pas encore de quelle école il s'agit. Ils prennent donc le gabarit commun
 * avec l'illustration Mentor Goal, plutôt qu'un champ posé nu au milieu d'un fond
 * gris.
 */
export function AuthCard({
	eyebrow,
	title,
	children,
	footer,
}: {
	eyebrow: string;
	title: string;
	children: ReactNode;
	footer?: ReactNode;
}) {
	return (
		<SplitLayout illustration={illustrationFor({ slug: "mentor-goal" })}>
			<div className="mg-rise max-w-sm">
				<Eyebrow>{eyebrow}</Eyebrow>
				<h1 className="mt-2 t-h1 text-mg-heading">{title}</h1>
				<div className="mt-8">{children}</div>
				{footer ? <div className="mt-6">{footer}</div> : null}
			</div>
		</SplitLayout>
	);
}

/**
 * Le champ de code : espacé, centré. On le saisit une fois, il doit être évident.
 *
 * ⚠️ Il portait aussi `text-[26px] font-semibold pl-[0.45em]`, que `.mg-field` (hors
 * couche) écrasait : le rendu réel a toujours été le 16/24 du champ. Depuis que
 * `.mg-field` vit dans `@layer components`, ces classes auraient repris effet ; elles
 * sont retirées pour garder le rendu affiché, et parce qu'une taille de police
 * écrite à la main n'a pas sa place dans un composant.
 *
 * L'échec (« Ce code n'est pas le bon ») s'écrit ICI, sous le champ, et le champ le
 * désigne (`aria-invalid`, `aria-describedby`). Il était posé à côté, dans la porte :
 * un lecteur d'écran revenu sur le champ pour corriger n'entendait pas pourquoi.
 */
export function CodeInput({
	value,
	onChange,
	label,
	error,
}: {
	value: string;
	onChange: (value: string) => void;
	label: string;
	error?: string | null;
}) {
	return (
		<div>
			<FieldLabel htmlFor="otp" label={label} />
			<input
				id="otp"
				inputMode="numeric"
				autoComplete="one-time-code"
				maxLength={6}
				placeholder="••••••"
				aria-invalid={error ? true : undefined}
				aria-describedby={error ? "otp-erreur" : undefined}
				value={value}
				onChange={(event) => onChange(event.target.value.replace(/\D/g, ""))}
				className="mg-field text-center tracking-[0.45em]"
			/>
			{error ? (
				<p id="otp-erreur" role="alert" className="t-body-md mt-2 text-mg-error-fg">
					{error}
				</p>
			) : null}
		</div>
	);
}
